import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'

import {STALE} from '#/state/queries'
import {useAgent} from '#/state/session'
import {type Slide} from '#/types/capsule'

export const CAPSULE_DRAFTS_RQKEY = 'capsuleDrafts'

/**
 * Shape of the capsule payload saved through app.bsky.draft. Mirrors
 * `app.bsky.draft.defs#draftCapsule`; slide payloads are the same ones that
 * get published into the capsule record (video assets stay device-bound).
 */
export type CapsuleDraftPayload = {
  title: string
  description?: string
  slides: Slide[]
  themeId?: string
  htmlThemeUrl?: string
  behaviorSettings?: Record<string, unknown>
  level?: string
  subject?: string
}

export type CapsuleDraft = {
  id: string
  capsule: CapsuleDraftPayload
  createdAt: string
  updatedAt: string
}

type RawDraftView = {
  id: string
  draft?: {
    capsule?: CapsuleDraftPayload
  }
  createdAt: string
  updatedAt: string
}

async function authFetchJson<T>(
  agent: ReturnType<typeof useAgent>,
  nsid: string,
  opts: {params?: Record<string, unknown>; init?: RequestInit} = {},
): Promise<T> {
  const accessJwt = agent.session?.accessJwt
  if (!accessJwt) throw new Error('Not authenticated')

  const base = agent.serviceUrl.toString().replace(/\/+$/, '')
  const url = new URL(`${base}/xrpc/${nsid}`)
  if (opts.params) {
    for (const [key, value] of Object.entries(opts.params)) {
      if (value === undefined) continue
      url.searchParams.set(key, String(value))
    }
  }
  const res = await fetch(url.toString(), {
    ...opts.init,
    headers: {
      Authorization: `Bearer ${accessJwt}`,
      ...(opts.init?.body ? {'Content-Type': 'application/json'} : {}),
      ...(opts.init?.headers ?? {}),
    },
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error((err as {message?: string}).message ?? `HTTP ${res.status}`)
  }
  return res.json() as Promise<T>
}

function toCapsuleDraft(raw: RawDraftView): CapsuleDraft | null {
  if (!raw.draft?.capsule) return null
  return {
    id: raw.id,
    capsule: raw.draft.capsule,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
  }
}

export function useCapsuleDraftsQuery() {
  const agent = useAgent()
  const did = agent.session?.did
  return useQuery({
    queryKey: [CAPSULE_DRAFTS_RQKEY, did ?? ''],
    enabled: !!did,
    staleTime: STALE.SECONDS.THIRTY,
    queryFn: async (): Promise<CapsuleDraft[]> => {
      const raw = (await authFetchJson<{drafts?: RawDraftView[]}>(
        agent,
        'app.bsky.draft.getDrafts',
        {params: {limit: 100}},
      ))
      return (raw.drafts ?? [])
        .map(toCapsuleDraft)
        .filter((d): d is CapsuleDraft => d !== null)
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
    },
  })
}

export function useSaveCapsuleDraft() {
  const agent = useAgent()
  const queryClient = useQueryClient()
  const did = agent.session?.did

  const invalidate = () => {
    if (!did) return
    void queryClient.invalidateQueries({queryKey: [CAPSULE_DRAFTS_RQKEY, did]})
  }

  const create = useMutation({
    mutationFn: async (capsule: CapsuleDraftPayload) => {
      const res = await authFetchJson<{id: string}>(
        agent,
        'app.bsky.draft.createDraft',
        {
          init: {
            method: 'POST',
            body: JSON.stringify({draft: {capsule}}),
          },
        },
      )
      return res
    },
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: async ({
      id,
      capsule,
    }: {
      id: string
      capsule: CapsuleDraftPayload
    }) => {
      await authFetchJson<Record<string, never>>(
        agent,
        'app.bsky.draft.updateDraft',
        {
          init: {
            method: 'POST',
            body: JSON.stringify({
              draft: {id, draft: {capsule}},
            }),
          },
        },
      )
    },
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: async (id: string) => {
      await authFetchJson<Record<string, never>>(
        agent,
        'app.bsky.draft.deleteDraft',
        {
          init: {
            method: 'POST',
            body: JSON.stringify({id}),
          },
        },
      )
    },
    onSuccess: invalidate,
  })

  return {create, update, remove}
}
